import {Environment, getTadaridaRestUrlByEnvironment} from '@aboutyou/config';
import {TADARIDA_USER_PERSIST_KEY} from '@aboutyou/user';
import type {Request} from 'express';
/* eslint-disable monorepo/no-internal-import */
import {createStaticConfig} from '@aboutyou/bootstrap/src/staticConfig/createStaticConfig';
import {StaticConfig} from '@aboutyou/bootstrap/src/staticConfig/types';

const PAY_ORDER_COOKIE_PREFIX = 'z_bnpl_pay_now_';
const HASH_PARAM_KEY = 'hash';

type SessionOrHash = {sessionId?: string; hash?: string} | undefined;

export type ProxyConfig =
  | {
      isAppLinksSubdomainRequest: true;
      tadaridaRestUrl: string;
      environment: Environment;
    }
  | {
      isAppLinksSubdomainRequest: false;
      tadaridaRestUrl: string;
      staticConfig: StaticConfig;
      environment: Environment;
    };

export const resolveAppLinksProxyConfig = (hostname: string): ProxyConfig => ({
  isAppLinksSubdomainRequest: true,
  ...getAppPOATargetUrl(hostname),
});

export const resolveWebProxyConfig = async (
  req: Request,
  traceId: string,
  hostname: string,
): Promise<ProxyConfig> => {
  const staticConfig = await createStaticConfig({
    traceId,
    originalRequest: req,
    hostname,
    abTestGroups: [],
    enableRealUserMode: false,
  });

  return {
    isAppLinksSubdomainRequest: false,
    staticConfig,
    tadaridaRestUrl: staticConfig.hostConfig.tadaridaRestUrl,
    environment: staticConfig.hostConfig.environment,
  };
};

const getAppPOATargetUrl = (
  hostname: string,
): {tadaridaRestUrl: string; environment: Environment} => {
  if (hostname.includes('staging.local')) {
    return {
      tadaridaRestUrl: getTadaridaRestUrlByEnvironment('staging'),
      environment: 'development',
    };
  }
  if (hostname.includes('staging.com')) {
    return {
      tadaridaRestUrl: getTadaridaRestUrlByEnvironment('staging'),
      environment: 'staging',
    };
  }
  return {
    tadaridaRestUrl: getTadaridaRestUrlByEnvironment('production'),
    environment: 'production',
  };
};

export const getHashToSessionIdHeader = (response: Response) => {
  return response.headers.get('x-ay-customer-session');
};

export const checkLoginNeeded = (sessionOrHash: SessionOrHash) => {
  if (sessionOrHash?.hash) {
    return false;
  }

  if (sessionOrHash?.sessionId) {
    return false;
  }

  return true;
};

export const getProxyRequestProperties = async ({
  req,
  tadaridaRestUrl,
  sessionOrHash,
}: {
  req: Request;
  tadaridaRestUrl: string;
  sessionOrHash: SessionOrHash;
}) => {
  const originalUrl = req.originalUrl;
  const targetUrl = `${tadaridaRestUrl}${originalUrl}`;
  const headers = buildPayOrderHeaders(req, sessionOrHash);
  const method = req.method;

  const body =
    method !== 'GET' && method !== 'HEAD'
      ? await collectRequestBody(req)
      : undefined;

  return {
    targetUrl,
    method,
    headers,
    body,
  };
};

export const getSessionOrHash = (
  req: Request,
  serverCookies: Record<string, string>,
): SessionOrHash => {
  const hash = req.query[HASH_PARAM_KEY] as string | undefined;

  const sessionCookie = serverCookies[TADARIDA_USER_PERSIST_KEY];
  const parsedSessionCookie = sessionCookie
    ? (JSON.parse(sessionCookie) as
        | {token: string; hash: string | undefined}
        | undefined)
    : undefined;

  if (hash) {
    if (parsedSessionCookie?.hash === hash && parsedSessionCookie.token) {
      return {sessionId: parsedSessionCookie.token};
    } else {
      return {hash};
    }
  } else if (parsedSessionCookie?.token) {
    return {sessionId: parsedSessionCookie.token};
  }
  return undefined;
};

const buildPayOrderHeaders = (req: Request, sessionOrHash: SessionOrHash) => {
  const rawHeaders = Object.fromEntries(
    Object.entries(req.headers).map(([key, value]) => [key, value]),
  );
  const headers: Record<string, string> = {
    ...rawHeaders,
    // eslint-disable-next-line no-nested-ternary
    ...(sessionOrHash?.hash
      ? {'X-Hash-ID': sessionOrHash.hash}
      : sessionOrHash?.sessionId
      ? {'X-Session-ID': sessionOrHash.sessionId}
      : {}),
  };

  const filteredCookies = filterPayOrderCookies(req.headers.cookie);
  if (filteredCookies) {
    headers.Cookie = filteredCookies;
  }

  return headers;
};

const collectRequestBody = (req: Request): Promise<Buffer> =>
  new Promise((resolve, reject) => {
    const chunks: Buffer[] = [];
    req.on('data', (chunk: Buffer) => chunks.push(chunk));
    req.on('end', () => resolve(Buffer.concat(chunks)));
    req.on('error', reject);
  });

// Headers that should not be forwarded from the upstream response
export const HOP_BY_HOP_HEADERS = new Set([
  'content-encoding',
  'transfer-encoding',
  'connection',
  'keep-alive',
  'content-length',
]);

const filterPayOrderCookies = (cookieHeader: string | undefined): string => {
  if (!cookieHeader) {
    return '';
  }

  return cookieHeader
    .split(';')
    .map(c => c.trim())
    .filter(c => c.startsWith(PAY_ORDER_COOKIE_PREFIX))
    .join('; ');
};
